import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAdminInvoicesApi } from "../../api/adminInvoicesApi";

// Initial State
const initialState = {
  invoices: [],
  totalInvoices: 0,
  loading: false,
  error: null,
  success: false,
  filters: {
    status: null,
    startDate: null,
    endDate: null,
    restaurantId: null,
    search: null,
  },
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
  },
};

// Async Thunk
export const fetchAdminInvoices = createAsyncThunk(
  "adminInvoices/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getAdminInvoicesApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invoices"
      );
    }
  }
);

// Slice
const adminInvoiceSlice = createSlice({
  name: "adminInvoices",
  initialState,
  reducers: {
    clearInvoices: (state) => {
      state.invoices = [];
      state.totalInvoices = 0;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        startDate: null,
        endDate: null,
        restaurantId: null,
        search: null,
      };
      state.pagination.page = 1;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchAdminInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.invoices = action.payload?.invoices || [];
        state.totalInvoices = action.payload?.totalInvoices || 0;
        state.pagination.totalPages = action.payload?.totalPages || 0;
        state.pagination.page = action.payload?.currentPage || 1;
        state.pagination.limit = action.payload?.limit || 10;
      })
      .addCase(fetchAdminInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        state.success = false;
        state.invoices = [];
        state.totalInvoices = 0;
        state.pagination.totalPages = 0;
      });
  },
});

// Export Actions
export const {
  clearInvoices,
  setFilters,
  clearFilters,
  setPagination,
  resetPagination,
  clearError,
  resetState,
} = adminInvoiceSlice.actions;

// ============ SELECTORS ============

// Basic Selectors
export const selectAllInvoices = (state) => state.adminInvoices.invoices;
export const selectTotalInvoices = (state) => state.adminInvoices.totalInvoices;
export const selectInvoiceLoading = (state) => state.adminInvoices.loading;
export const selectInvoiceError = (state) => state.adminInvoices.error;
export const selectInvoiceSuccess = (state) => state.adminInvoices.success;
export const selectFilters = (state) => state.adminInvoices.filters;
export const selectPagination = (state) => state.adminInvoices.pagination;

// Filtered Selectors
export const selectInvoicesByStatus = (state, status) => {
  if (!status) return state.adminInvoices.invoices;
  return state.adminInvoices.invoices.filter(
    (invoice) => invoice.status === status
  );
};

export const selectPaidInvoices = (state) => {
  return state.adminInvoices.invoices.filter(
    (invoice) => invoice.status === "PAID"
  );
};

export const selectPendingInvoices = (state) => {
  return state.adminInvoices.invoices.filter(
    (invoice) => invoice.status === "PENDING" || invoice.status === "PAYMENT_SUBMITTED"
  );
};

export const selectExpiredInvoices = (state) => {
  return state.adminInvoices.invoices.filter(
    (invoice) => invoice.status === "EXPIRED"
  );
};

// Calculation Selectors
export const selectTotalAmount = (state) => {
  return state.adminInvoices.invoices.reduce(
    (total, invoice) => total + (invoice.amount || 0),
    0
  );
};

export const selectPaidAmount = (state) => {
  return state.adminInvoices.invoices
    .filter((invoice) => invoice.status === "PAID")
    .reduce((total, invoice) => total + (invoice.amount || 0), 0);
};

export const selectPendingAmount = (state) => {
  return state.adminInvoices.invoices
    .filter((invoice) => invoice.status === "PENDING" || invoice.status === "PAYMENT_SUBMITTED")
    .reduce((total, invoice) => total + (invoice.amount || 0), 0);
};

export const selectInvoiceCount = (state) => {
  return {
    total: state.adminInvoices.totalInvoices,
    paid: state.adminInvoices.invoices.filter((inv) => inv.status === "PAID").length,
    pending: state.adminInvoices.invoices.filter(
      (inv) => inv.status === "PENDING" || inv.status === "PAYMENT_SUBMITTED"
    ).length,
    expired: state.adminInvoices.invoices.filter(
      (inv) => inv.status === "EXPIRED"
    ).length,
  };
};

// Restaurant specific selectors
export const selectInvoicesByRestaurant = (state, restaurantId) => {
  return state.adminInvoices.invoices.filter(
    (invoice) => invoice.restaurantId?._id === restaurantId
  );
};

export const selectRestaurantInvoiceSummary = (state, restaurantId) => {
  const invoices = state.adminInvoices.invoices.filter(
    (invoice) => invoice.restaurantId?._id === restaurantId
  );
  
  return {
    total: invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    paid: invoices.filter((inv) => inv.status === "PAID").length,
    pending: invoices.filter(
      (inv) => inv.status === "PENDING" || inv.status === "PAYMENT_SUBMITTED"
    ).length,
    lastInvoice: invoices[invoices.length - 1] || null,
  };
};

export default adminInvoiceSlice.reducer;